import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Agrega productos al carrito desde los componentes propios de Zendelaya
 * (panel de tallas de las tarjetas, detalle del look y lista de productos del look)
 * y avisa al tema con el mismo evento que usa el formulario de producto de Horizon.
 *
 * Así el contador del carrito, el panel lateral (incluido el paso de "vacío" a "con productos")
 * y la apertura automática del carrito se actualizan sin recargar la página.
 */

const root = window.Shopify?.routes?.root ?? '/';

function cartAddUrl() {
  return window.Theme?.routes?.cart_add_url ?? `${root}cart/add.js`;
}

function cartUrl() {
  return `${root}cart.js`;
}

/** Secciones del carrito que el tema re-renderiza con la respuesta de /cart/add */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

async function fetchCart() {
  const response = await fetch(cartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

/**
 * @param {Array<{id: number | string, quantity: number}>} items
 * @param {{ source?: EventTarget, sourceName?: string }} [options]
 *   source: elemento desde el que se dispara el evento (el botón pulsado).
 * @returns {Promise<{ cart: any, added: any }>}
 */
export async function addToCart(items, { source = document, sourceName = 'zendelaya' } = {}) {
  /* Recomendación de Encuentra tu talla de ese producto (si la hubo): se guarda en la línea como propiedad
     oculta "_zd_rec" para unirla después con el pedido y la devolución (fase B, sin datos personales) */
  const registro = window.ZendelayaTalla?.registroPara?.(source) || null;
  const lines = items.map((item) => {
    const line = { id: Number(item.id), quantity: item.quantity || 1 };
    const properties = item.properties || (registro && items.length === 1 ? { _zd_rec: registro } : null);
    if (properties) line.properties = properties;
    return line;
  });
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const sectionIds = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  /** @type {Record<string, any>} */
  const payload = { items: lines };
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  try {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const added = await response.json();

    if (!response.ok || added.status) {
      // Error de Shopify (p. ej. sin existencias): sincroniza el tema con el carrito real
      const cart = await fetchCart().catch(() => null);
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
        detail: { didError: true, items: cart?.items ?? [], source: sourceName, itemCount },
      });
      // userMessage: texto de Shopify listo para mostrar al cliente (p. ej. "Ya tienes todas las unidades...")
      throw Object.assign(new Error(added.message || 'cart'), { userMessage: added.description || '' });
    }

    const cart = await fetchCart();
    /* Aviso para el registro: talla agregada desde una recomendación */
    (added.items || [added]).forEach((it) => {
      const rec = it?.properties?._zd_rec;
      if (!rec) return;
      const talla = (it.options_with_values || []).find((o) => /^(talla|size)$/i.test(String(o.name).trim()));
      document.dispatchEvent(new CustomEvent('zendelaya:agregado', { detail: { rec, producto: it.handle, talla: talla ? talla.value : null } }));
    });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: sourceName,
        itemCount,
        sections: added.sections,
      },
    });

    return { cart, added };
  } catch (error) {
    // No hace nada si la promesa ya se resolvió arriba
    deferred.reject(error);
    throw error;
  }
}

/**
 * Cambia la cantidad de una línea del carrito (0 la quita) desde el carrito propio de Zendelaya
 * (snippets/zendelaya-carrito.liquid) y avisa al tema con el mismo evento que su carrito, para que el
 * contador del icono y las demás piezas se actualicen.
 * @param {string} key  Clave de la línea (item.key de /cart.js)
 * @param {number} quantity
 * @param {{ source?: EventTarget }} [options]
 */
export async function changeLine(key, quantity, { source = document } = {}) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: String(key), quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(window.Theme?.routes?.cart_change_url ?? `${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(key), quantity }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const cart = await fetchCart().catch(() => null);
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
        detail: { didError: true, items: cart?.items ?? [], source: 'zendelaya-carrito' },
      });
      throw Object.assign(new Error(data.message || 'cart'), { userMessage: data.description || '', cart });
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { didError: false, items: data.items, itemCount: data.item_count, source: 'zendelaya-carrito' },
    });
    return data;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* Acceso global para el código de {% javascript %} de los snippets: Shopify compila ese código
   y convierte import() en require(), que no existe en el navegador. */
window.ZendelayaCart = { addToCart, changeLine, getCart: fetchCart };
document.dispatchEvent(new CustomEvent('zendelaya:cart-ready'));
